// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import {
  hashValues,
  number,
  route,
  string,
  types,
  union,
} from "react-router-typesafe-routes/dom";

export const EmailVerification = route("verify");

export const AfterLogin = route("after-login", {
  // search params are optional by default so it's fine if these are missing
  // (which they will be when login succeeds)
  searchParams: { error: string(), error_description: string() },
});

export const ReturnToPathFragment = route("", {
  searchParams: { returnToPath: string() },
});

export const OrijinSSOPage = route("orijin/sso");

export const EdovoLandingPage = route("edovo/:token", {
  // Edovo echoes this back when a resident returns from a referral
  searchParams: { sessionResumeId: string() },
});

export const EGT = route(
  "earned-good-time",
  {},
  {
    Definition: route("definition/:pageSlug"),
    Intro: route("intro"),
    AllMonths: route("report/all"),
    MonthlyReport: route("report/:reportDate"),
  },
);

export const ProgramCatalog = route(
  "programs",
  {},
  {
    InPerson: route(""),
    OnMyTablet: route("on-my-tablet"),
  },
);

export const UsTnMoreInformation = route(
  "more-information",
  {},
  {
    About: route("about"),
    ImportantDates: route("dates", { hash: ["expiration-date"] }),
    Credits: route("credits"),
  },
);

export const UsArMoreInformation = route(
  "more-information",
  {},
  {
    ImportantDates: route("important-dates", {
      searchParams: { backTarget: string() },
    }),
    EarnedReleaseCredits: route("earned-release-credits"),
  },
);

export const UsAzMoreInformation = route(
  "more-information",
  {},
  {
    Intro: route("intro"),
    About: route("about"),
    ImportantDates: route("important-dates", { hash: [] }),
    DPR: route("drug-program-release"),
    ION: route("inside-out-network"),
  },
);

export const UsAzClassificationPoints = route(
  "classification",
  {},
  { FAQ: route("faq") },
);

export const UsNcRNA = route(
  "rna",
  {},
  {
    Landing: route(""),
    ConfirmIdentity: route("is-this-you"),
    FormPage: route("page/:pageNum", {
      params: { pageNum: number().defined() },
    }),
  },
);

export const UsNdMoreInformation = route("more-information/:pageSlug", {
  params: {
    pageSlug: union(["important-dates"] as const).defined(),
  },
});

export const UsNeMoreInformation = route("more-information/:pageSlug", {
  params: {
    pageSlug: union([
      "mmtd",
      "ped",
      "trd",
      "gbmd",
      "lb191",
      "jailCredits",
    ] as const),
  },
});

export const UsNeReentryChecklist = route("reentry-checklist");

export const UsNeCheckInTool = route("check-in-tool");

export const ReentryAssessment = route("reentry-assessment");

export const ResourceExplorer = route(
  "resources",
  {},
  {
    CategoryResults: route(
      "categories/:category",
      {
        searchParams: {
          subcategories: string().defined().array(),
          tags: string().defined().array(), // Only US_NYC reads tags; US_MA filters on the other params.
          counties: string().defined().array(),
          gender: string().defined().array(),
          lengthOfStay: string().defined().array(),
          rent: string().defined().array(),
          accepts: string().defined().array(),
        },
      },
      {
        Detail: route(":resourceId", {
          params: { resourceId: number().defined() },
          searchParams: { backTarget: string(), visitedResourceIds: string() },
        }),
      },
    ),
  },
);

export const usMaReentryGuideTopicSlugs = ["id-documents", "housing"] as const;

export const UsMaReentry = route(
  "reentry",
  {},
  {
    Overview: route(""),
    Questionnaire: route("questionnaire", { hash: hashValues() }),
    Checklist: route("checklist"),
    Guide: route("guide/:topicSlug", {
      params: {
        topicSlug: union(usMaReentryGuideTopicSlugs),
      },
    }),
    Resources: ResourceExplorer,
  },
);

export const UsCoMoreInformation = route(
  "more-information",
  {
    searchParams: { backTarget: string() },
  },
  {
    EarnedTime: route("earned-time"),
  },
);

export const State = route(":stateSlug", types(ReturnToPathFragment), {
  Resident: route(
    ":personPseudoId",
    {},
    {
      EGT,
      ProgramCatalog,
      ReentryAssessment,
      UsArMoreInformation,
      UsAzClassificationPoints,
      UsAzMoreInformation,
      UsCoMoreInformation,
      UsNcRNA,
      UsNdMoreInformation,
      UsMaReentry,
      UsNeCheckInTool,
      UsNeMoreInformation,
      UsNeReentryChecklist,
      ResourceExplorer,
      UsTnMoreInformation,
    },
  ),
  Search: route("search"),
});

export const SiteRoot = route("", types(ReturnToPathFragment));

export const StateSelect = route("state-select");
